/**
 * View Toggle Functionality for Product Collections
 * 
 * Handles:
 * - Switching between grid and list view
 * - Saving user preferences in localStorage
 * - Updating product listings dynamically
 * - Quantity controls for list view
 * - Price updates when changing variants
 * - Support for filtering and pagination
 */

// View Toggle Functionality
function initViewToggle() {
    const toggleButtons = document.querySelectorAll('.view-toggle-btn');

    // Get saved view preference or default to grid
    let currentView = localStorage.getItem('collection-view-preference') || 'grid';

    // Set initial view
    setView(currentView);

    // Add click handlers to toggle buttons
    toggleButtons.forEach((button) => {
        button.addEventListener('click', function () {
            const view = this.dataset.view;
            setView(view);
            localStorage.setItem('collection-view-preference', view);
        });
    });

    function setView(view) {
        // Get fresh references to product listings each time
        const productListings = document.querySelectorAll('.productListing, .productListing2');

        // Update button states
        toggleButtons.forEach((btn) => {
            btn.classList.toggle('active', btn.dataset.view === view);
        });

        // Update product listings
        productListings.forEach((listing) => {
            if (view === 'list') {
                listing.classList.add('list-view');
                listing.classList.remove('productGrid');

                // Convert grid items to list items
                convertToListView(listing);
            } else {
                listing.classList.remove('list-view');
                listing.classList.add('productGrid');

                // Restore grid layout
                restoreGridView(listing);
            }
        });
    }

    // Return setView function for external use
    return { setView };
}

function convertToListView(listing) {
    const products = listing.querySelectorAll('.product');

    products.forEach((product) => {
        if (!product.querySelector('.list-view-content')) {
            const originalCard = product.querySelector('.card, .product-item');
            if (originalCard) {
                createListViewLayout(product, originalCard);
            }
        }
    });
}

function createListViewLayout(productElement, originalCard) {
    // Get product data from the actual HTML structure
    const productLink = originalCard.querySelector('.card-title');
    const productTitle = originalCard.querySelector('.card-title .text, .card-title');
    const productPrice = originalCard.querySelector('.price-item, .price .price-item, .price-item--regular');

    // Extract Art.Nr from card-information__group
    const cardInfoGroup = originalCard.querySelector('.card-information__group');
    let artNr = 'N/A';
    if (cardInfoGroup) {
        const artNrParagraphs = cardInfoGroup.querySelectorAll('p');
        for (let p of artNrParagraphs) {
            if (p.textContent.includes('Art.nr:')) {
                artNr = p.textContent.replace('Art.nr:', '').trim();
                break;
            }
        }
    }

    // Check for replacement product information
    let replacementInfo = '';

    // Look for spans containing replacement text
    const allSpans = originalCard.querySelectorAll('span');
    for (let span of allSpans) {
        const spanText = span.textContent.toLowerCase();
        if (spanText.includes('utgått') || spanText.includes('ersätts av')) {
            replacementInfo = span.outerHTML;
            break;
        }
    }

    // Alternative check: look for links with the distinctive blue color style
    if (!replacementInfo) {
        const replacementLink = originalCard.querySelector('a[style*="color:#2669a7"], a[style*="color: #2669a7"]');
        if (replacementLink) {
            const parentSpan = replacementLink.closest('span');
            if (parentSpan) {
                // Verify this is indeed a replacement link by checking the text context
                const parentText = parentSpan.textContent.toLowerCase();
                if (parentText.includes('utgått') || parentText.includes('ersätts av')) {
                    replacementInfo = parentSpan.outerHTML;
                }
            }
        }
    }

    // Get unit selector - try multiple selectors
    const customSelect = originalCard.querySelector('.custom-select-container');
    const regularSelect = originalCard.querySelector('select[name="id"], .variant-selector, .size-drop-down');

    if (!productLink || !productTitle) return;

    // Hide original content
    originalCard.style.display = 'none';

    // Format price first to determine what controls to show
    let formattedPrice = '<div class="card-action"><a class="button button-2" href="/account/login">Logga in för pris</a></div>';
    let hasPricing = false;
    if (productPrice) {
        let priceText = productPrice.textContent || productPrice.innerHTML;
        // Handle price with sup tags (like 40<sup>00</sup> should become 40,00)
        if (productPrice.querySelector('sup')) {
            const mainPrice = productPrice.childNodes[0].textContent || '';
            const supPrice = productPrice.querySelector('sup').textContent || '';
            priceText = mainPrice + ',' + supPrice;
        }
        // Clean and format price
        priceText = priceText.replace(/\s*(kr|SEK)\s*$/i, '').trim();
        if (priceText && priceText !== '' && priceText.includes("Logga in för pris") === false) {
            formattedPrice = priceText + ' kr';
            hasPricing = true;
        } else if (priceText.includes("Logga in för pris")) {
            formattedPrice = '';
        }
    }

    if (!formattedPrice) {
        formattedPrice = '';
    }

    // Create unit selector HTML - use the regular select instead of custom dropdown
    let unitSelectorHTML = '<span>Standard</span>';
    let actionHTML = '';

    if (replacementInfo) {
        // Product is expired or has replacement - show replacement info instead of buy controls
        actionHTML = `
      <div class="list-item-footer">
        <div class="list-item-replacement">${replacementInfo}</div>
      </div>
    `;
    } else if (!hasPricing) {
        // No valid price available - don't show buy controls
        actionHTML = '';
    } else {
        // Normal product with pricing - show unit selector and buy button
        if (regularSelect) {
            // Clone the regular select and simplify it
            const clonedSelect = regularSelect.cloneNode(true);
            if (clonedSelect.id) clonedSelect.id = clonedSelect.id + '-list-view';
            clonedSelect.className = 'list-view-select';
            unitSelectorHTML = clonedSelect.outerHTML;
        } else if (customSelect) {
            // Extract options from custom select and create a simple select
            const options = customSelect.querySelectorAll('.option');
            if (options.length > 0) {
                let selectHTML = '<select class="list-view-select">';
                options.forEach(option => {
                    const value = option.dataset.value || '';
                    const text = option.textContent.trim();
                    const selected = option.classList.contains('selected-option') ? 'selected' : '';
                    selectHTML += `<option value="${value}" ${selected}>${text}</option>`;
                });
                selectHTML += '</select>';
                unitSelectorHTML = selectHTML;
            }
        }

        // Get the form for add to cart functionality
        const addToCartForm = originalCard.querySelector('form[action="/cart/add"]');
        const formId = addToCartForm ? addToCartForm.id : '';

        actionHTML = `
      <div class="list-item-controls">
        <div class="list-item-unit-selector">
          ${unitSelectorHTML}
        </div>
        <div class="list-item-quantity">
          <div class="quantity-input">
            <button class="quantity-btn quantity-decrease">−</button>
            <input type="number" class="quantity-field" value="1" min="1" max="99">
            <button class="quantity-btn quantity-increase">+</button>
          </div>
        </div>
      </div>
      <div class="list-item-footer">
        <div class="list-item-price">
          ${formattedPrice}
        </div>
        <div class="list-item-action">
          <button class="btn btn-primary list-add-to-cart" data-form-id="${formId}">KÖP</button>
        </div>
      </div>
    `;
    }

    // Create simplified list view HTML
    const listViewHTML = `
    <div class="list-view-content">
      <div class="list-item-content">
        <div class="list-item-title">
          <a href="${productLink.href}">${productTitle.textContent.trim()}</a>
        </div>
        <div class="list-item-art-nr">
          <span>Art.Nr: ${artNr}</span>
        </div>
        ${actionHTML || `<div class="list-item-price">${formattedPrice}</div>`}
      </div>
    </div>
  `;

    productElement.insertAdjacentHTML('beforeend', listViewHTML);
}

function restoreGridView(listing) {
    const products = listing.querySelectorAll('.product');

    products.forEach((product) => {
        const listViewContent = product.querySelector('.list-view-content');
        const originalCard = product.querySelector('.card, .product-item');

        if (listViewContent) {
            listViewContent.remove();
        }

        if (originalCard) {
            originalCard.style.display = '';
        }
    });
}

function initListViewControls() {
    // Handle unit selector changes for price updates
    document.addEventListener('change', function (e) {
        if (e.target.classList.contains('list-view-select')) {
            const listContent = e.target.closest('.list-view-content');
            const productElement = e.target.closest('.product');
            const priceDisplay = listContent.querySelector('.list-item-price');

            // Find the original form and variant selector
            const originalForm = productElement.querySelector('form[action="/cart/add"]');
            if (originalForm) {
                const variantSelect = originalForm.querySelector('select[name="id"]');

                if (variantSelect) {
                    // Sync the selection
                    variantSelect.value = e.target.value;

                    // Try multiple ways to trigger price update
                    variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    variantSelect.dispatchEvent(new Event('input', { bubbles: true }));

                    // Also try triggering on the custom select if it exists
                    const customSelect = originalForm.querySelector('.custom-select-container');
                    if (customSelect) {
                        const customTrigger = customSelect.querySelector('.select-trigger, .custom-select-trigger');
                        if (customTrigger) {
                            customTrigger.click();
                            setTimeout(() => {
                                const option = customSelect.querySelector(`[data-value="${e.target.value}"]`);
                                if (option) {
                                    option.click();
                                }
                            }, 50);
                        }
                    }

                    // Wait longer and try multiple price selectors
                    setTimeout(() => {
                        // Try multiple price selectors
                        const priceSelectors = [
                            '.price-item',
                            '.price .price-item',
                            '.price-item--regular',
                            '.card-information .price',
                            '.price',
                            '.product-price',
                            '[data-price]'
                        ];

                        let updatedPrice = null;
                        for (let selector of priceSelectors) {
                            updatedPrice = originalForm.querySelector(selector) || productElement.querySelector(selector);
                            if (updatedPrice) {
                                break;
                            }
                        }

                        if (updatedPrice && priceDisplay) {
                            let priceText = updatedPrice.textContent || updatedPrice.innerHTML;

                            // Handle price with sup tags
                            if (updatedPrice.querySelector('sup')) {
                                const mainPriceNode = Array.from(updatedPrice.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
                                const mainPrice = mainPriceNode ? mainPriceNode.textContent.trim() : '';
                                const supPrice = updatedPrice.querySelector('sup').textContent || '';
                                priceText = mainPrice + ',' + supPrice;
                            }

                            // Clean and format price
                            priceText = priceText.replace(/\s*(kr|SEK)\s*$/i, '').trim();
                            if (priceText && priceText !== '' && priceText !== '') {
                                priceDisplay.textContent = priceText + ' kr';
                            } else {
                                priceDisplay.textContent = '';
                            }
                        } else {
                        }
                    }, 300);
                }
            }
        }
    });

    document.addEventListener('click', function (e) {
        // Quantity controls
        if (e.target.classList.contains('quantity-decrease')) {
            const input = e.target.parentNode.querySelector('.quantity-field');
            const currentVal = parseInt(input.value) || 1;
            if (currentVal > 1) {
                input.value = currentVal - 1;
            }
        }

        if (e.target.classList.contains('quantity-increase')) {
            const input = e.target.parentNode.querySelector('.quantity-field');
            const currentVal = parseInt(input.value) || 1;
            if (currentVal < 99) {
                input.value = currentVal + 1;
            }
        }

        // Add to cart button
        if (e.target.classList.contains('list-add-to-cart')) {
            const listContent = e.target.closest('.list-view-content');
            const quantity = listContent.querySelector('.quantity-field').value;
            const unitSelect = listContent.querySelector('.list-view-select');
            const productElement = e.target.closest('.product');
            const formId = e.target.dataset.formId;

            // Find the original form
            let originalForm = null;
            if (formId) {
                originalForm = document.getElementById(formId);
            }

            // Fallback: find any form in the product card
            if (!originalForm) {
                originalForm = productElement.querySelector('form[action="/cart/add"]');
            }

            if (originalForm) {
                // Update quantity
                const quantityInput = originalForm.querySelector('input[name="quantity"], .quantity-box');
                if (quantityInput) {
                    quantityInput.value = quantity;
                }

                // Update variant selection if unit selector exists
                if (unitSelect && unitSelect.value) {
                    const variantSelect = originalForm.querySelector('select[name="id"]');
                    if (variantSelect) {
                        variantSelect.value = unitSelect.value;
                        // Trigger change event to update price if needed
                        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }

                // Submit the form
                const submitButton = originalForm.querySelector('button[type="submit"], button[name="add"], .add-to-cart-btn');
                if (submitButton) {
                    submitButton.click();
                } else {
                    // Fallback: submit the form directly
                    originalForm.submit();
                }
            }
        }
    });
}

// Initialize view toggle and list controls
document.addEventListener('DOMContentLoaded', function () {
    window.viewToggleInstance = initViewToggle();
    initListViewControls();
    initPaginationObserver();
});

// Global function to reapply current view (can be called after filtering)
window.reapplyViewPreference = function () {
    if (window.viewToggleInstance) {
        const currentView = localStorage.getItem('collection-view-preference') || 'grid';
        setTimeout(() => {
            window.viewToggleInstance.setView(currentView);
        }, 200);
    }
};

// Handle dynamically loaded products from pagination and filtering
function initPaginationObserver() {
    // Create a mutation observer to watch for new products being added or content being replaced
    const observer = new MutationObserver((mutations) => {
        let shouldReapplyView = false;
        let hasNewProducts = false;

        mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                mutation.addedNodes.forEach((node) => {
                    // Check if the added node is a product or contains products
                    if (node.nodeType === Node.ELEMENT_NODE) {

                        // Check for complete product listing replacement (filtering)
                        if (node.classList && (node.classList.contains('productListing') || node.classList.contains('productListing2'))) {
                            shouldReapplyView = true;
                            return;
                        }

                        // Check for products within the added node
                        const foundProducts = node.querySelectorAll ? node.querySelectorAll('.product') : [];
                        const newProducts = [];

                        // Check if the node itself is a product
                        if (node.classList && node.classList.contains('product')) {
                            newProducts.push(node);
                        }

                        newProducts.push(...foundProducts);

                        if (newProducts.length > 0) {
                            hasNewProducts = true;

                            // Check if we're currently in list view
                            const currentView = localStorage.getItem('collection-view-preference') || 'grid';

                            if (currentView === 'list') {
                                // Apply list view to new products
                                newProducts.forEach((product) => {
                                    const originalCard = product.querySelector('.card, .product-item');
                                    if (originalCard && !product.querySelector('.list-view-content')) {
                                        createListViewLayout(product, originalCard);
                                    }
                                });

                                // Also make sure the parent container has the right classes
                                const parentListing = newProducts[0].closest('.productListing, .productListing2');
                                if (parentListing) {
                                    parentListing.classList.add('list-view');
                                    parentListing.classList.remove('productGrid');
                                }
                            }
                        }
                    }
                });
            }
        });

        // If product listings were replaced, reapply the current view to everything
        if (shouldReapplyView && window.viewToggleInstance) {
            setTimeout(() => {  // Small delay to ensure DOM is fully updated
                const currentView = localStorage.getItem('collection-view-preference') || 'grid';
                window.viewToggleInstance.setView(currentView);
            }, 100);
        }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
}